import { Autocomplete, AutocompleteProps, InputBase, ListItemText, styled } from "@mui/material";

import { ConditionOperator as ConditionOperatorEnum } from "@sinch/core/entities/serverEnums";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { useFontSizeCalculate } from "@sinch/hooks/utilities/useFontSizeCalculate";
import { ConditionCategoryOperator, ConditionOperator } from "@sinch/types/sinch.types";
import { identity, isNil } from "ramda";
import { isNotEmpty, isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback, useEffect, useMemo } from "react";
import { useOperator } from "@sinch/components/application/ConditionBuilder/useOperator";
import { ConditionSelectProps } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import { RateConditionCategoryOperators } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.graphql";
import {
  RateConditionCategoryOperatorsQuery,
  RateConditionCategoryOperatorsQueryVariables,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.types";

export type ConditionOperatorSelectProps = ConditionSelectProps<ConditionOperator | ConditionOperatorEnum | null> & {
  operatorCategory?: ConditionCategoryOperator;
  onBlur?: () => void;
};

export const ConditionOperatorSelect = ({
  onChange,
  onOpenChange,
  onFocus,
  value,
  operatorCategory,
  onBlur,
  readOnly,
}: ConditionOperatorSelectProps) => {
  const { getOperatorLabel, getOperatorDescription } = useOperator();

  const { data } = useSuspenseQueryHandler<
    RateConditionCategoryOperatorsQuery,
    RateConditionCategoryOperatorsQueryVariables
  >(RateConditionCategoryOperators);

  const options = useMemo<readonly (ConditionOperator | ConditionOperatorEnum)[]>(
    () => data?.rateConditionCategoryOperators.find((o) => o.categoryOperator === operatorCategory)?.operators ?? [],
    [data, operatorCategory]
  );

  useEffect(() => {
    if (isNil(value) && isNotEmpty(options)) {
      onChange(options[0]);
    }
  }, [options, value, onChange]);

  const handleGetOptionLabel = useCallback(
    (operator: ConditionOperator | ConditionOperatorEnum | null) =>
      isNotNilOrEmpty(operator) ? getOperatorLabel(operator as ConditionOperatorEnum) : "",
    [getOperatorLabel]
  );

  const handleChange = useCallback(
    (_: unknown, val: ConditionOperator | ConditionOperatorEnum | null) => {
      onChange(val as ConditionOperator);
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleOpen = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  const placeholderWidth = useFontSizeCalculate(handleGetOptionLabel(value), 14);

  const handleRenderInput = useCallback<
    AutocompleteProps<ConditionOperator | ConditionOperatorEnum | null, false, boolean, false>["renderInput"]
  >(
    (params) => (
      <InputBase
        {...params.InputProps}
        endAdornment={params.InputProps.endAdornment}
        fullWidth
        inputProps={{
          ...params.inputProps,
          "data-cy": "ConditionInput-operatorSelect",
          readOnly: true,
          sx: {
            textAlign: "center",
            ".MuiAutocomplete-inputRoot &.MuiAutocomplete-input": {
              width: placeholderWidth && `${Math.ceil(placeholderWidth)}px`,
            },
          },
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        readOnly
      />
    ),
    [onBlur, onFocus, placeholderWidth]
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key: any },
      option: ConditionOperator | ConditionOperatorEnum | null
    ) => (
      <li {...props}>
        <ListItemText
          primary={handleGetOptionLabel(option)}
          secondary={getOperatorDescription(option as ConditionOperatorEnum)}
          slotProps={{
            primary: {
              sx: (theme) => ({
                fontSize: theme.typography.pxToRem(14),
                fontWeight: "600",
              }),
            },
          }}
          sx={{ m: 0 }}
        />
      </li>
    ),
    [handleGetOptionLabel, getOperatorDescription]
  );

  return (
    <ConditionOperatorAutocomplete<ConditionOperator | ConditionOperatorEnum | null, false, boolean, false>
      disableClearable
      disabled={!operatorCategory}
      filterOptions={identity}
      fullWidth
      getOptionLabel={handleGetOptionLabel}
      inputValue={handleGetOptionLabel(value)}
      onChange={handleChange}
      onClose={handleClose}
      onOpen={handleOpen}
      options={options}
      readOnly={readOnly}
      renderInput={handleRenderInput}
      renderOption={renderOption}
      slotProps={{
        popper: {
          "data-cy": "ConditionInput-operatorSelect-dropdown",
          style: { width: "fit-content" },
        },
      }}
      value={operatorCategory ? (value ?? null) : null}
    />
  );
};

const ConditionOperatorAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "&.MuiAutocomplete-hasPopupIcon>.MuiInputBase-root": {
    paddingLeft: theme.spacing(1),
    paddingRight: "20px",
  },
})) as typeof Autocomplete;
